import { ArrowDown, ArrowUp } from 'react-feather';
import { ReactNode, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { getAutoHeightDuration, useHeight } from './UI/Animations';
import UI from './UI';

interface ShowMoreToggleProps {
  children?: ReactNode;
  justify?: 'start' | 'center' | 'end';
  maxHeight?: number;
}

const ShowMoreToggle = ({ children, justify = 'center', maxHeight = 300 }: ShowMoreToggleProps) => {
  const { t } = useTranslation('common');

  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const contentHeight = useHeight(contentRef);

  const [expanded, setExpanded] = useState(false);

  const minHeight = Math.min(contentHeight, maxHeight);

  const variants = {
    open: { height: contentHeight },
    collapsed: { height: Math.min(contentHeight, maxHeight) },
  };

  return (
    <UI.Div>
      <motion.div
        initial={expanded ? 'open' : 'collapsed'}
        animate={expanded ? 'open' : 'collapsed'}
        variants={variants}
        inherit={false}
        transition={{
          ease: 'easeOut',
          duration: getAutoHeightDuration(contentHeight) / 1000,
        }}
        ref={containerRef}
        style={{
          minHeight,
          overflow: 'hidden',
          mask: expanded || contentHeight <= maxHeight ? 'none' : 'linear-gradient(black 50%, transparent 100%)',
        }}
      >
        <UI.Div
          ref={contentRef}
        >
          {children}
        </UI.Div>
      </motion.div>
      {contentHeight > maxHeight && (
        <UI.FlexContainer sc={{ justifyContent: justify, mt: 3 }}>
          <UI.Button
            onClick={() => setExpanded((expanded) => !expanded)}
            sc={{ borderRadius: 'xl', size: 'sm', outline: true }}
          >
            <UI.Icon>
              {expanded ? <ArrowUp /> : <ArrowDown />}
            </UI.Icon>
            {' '}
            {expanded ? t('read_more.show_less') : t('read_more.show_more')}
          </UI.Button>
        </UI.FlexContainer>
      )}
    </UI.Div>
  );
};

export default ShowMoreToggle;
